import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { OwnerInput } from "@/lib/validations/owner";
import { getCurrentClinicCustomerId } from "@/lib/utils/clinic-context";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { invalidatePatientsCache } from "@/lib/react-query/invalidatePatientsCache";
import { rollbackOwnerOrQueue } from "@/lib/patients/ownerRollbackQueue";
import { useTranslation } from "react-i18next";

export interface Owner {
  id: string;
  name: string;
  gender_id: string | null;
  document_type_id: string | null;
  document: string | null;
  birth_date: string | null;
  profession: string | null;
  notes: string | null;
  marketing_consent: boolean;
  lead_source_id: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  phones?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emails?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addresses?: any[];
}

export interface OwnerWithContacts extends Owner {
  notes: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  phones: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emails: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addresses: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pets?: any[];
}

export interface PaginatedOwners {
  data: OwnerWithContacts[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

// Listar owners com busca e filtros
export const useOwners = (searchTerm?: string, page: number = 1, pageSize: number = 25) => {
  const { data: customerId } = useCurrentClinicCustomerId();
  const { t } = useTranslation("patients");

  return useQuery<PaginatedOwners>({
    queryKey: ["owners", customerId, searchTerm, page, pageSize],
    enabled: !!customerId,
    queryFn: async () => {
      if (!customerId) throw new Error(t("phase8.owners.errors.noClinicSelected"));

      const createBaseQuery = () =>
        supabase
          .from("owners")
          .select(
            `
          *,
          owner_customer:owners_customers!inner(
            customer_id,
            deleted_at
          ),
          phones:owners_phones(id, phone_number, is_whatsapp, is_primary),
          emails:owners_emails(id, email, is_primary),
          addresses:owners_addresses(*, city:cities(id, name, state:states(id, name, code)))
        `,
            { count: "exact" }
          )
          .eq("owner_customer.customer_id", customerId)
          .is("owner_customer.deleted_at", null)
          .is("deleted_at", null)
          .order("name", { ascending: true, nullsFirst: false });

      const normalizedSearch = searchTerm?.trim();
      const offset = (page - 1) * pageSize;

      const baseQuery = createBaseQuery();
      const query = normalizedSearch
        ? baseQuery.ilike("name", `%${normalizedSearch}%`)
        : baseQuery;

      const { data: queryData, error, count } = await query.range(offset, offset + pageSize - 1);

      if (error) throw error;

      return {
        data: ((queryData ?? []).map((owner) => {
          const { owner_customer: _ownerCustomer, ...rest } = owner;
          return {
            ...rest,
            pets: [],
            notes: null,
            addresses: owner.addresses ?? [],
          };
        })) as OwnerWithContacts[],
        totalCount: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize),
        currentPage: page,
        pageSize,
      };
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
};

// Obter detalhes de um owner
export const useOwner = (id: string) => {
  const { data: customerId } = useCurrentClinicCustomerId();
  const { t } = useTranslation("patients");

  return useQuery({
    queryKey: ["owner", customerId, id],
    enabled: !!id && !!customerId,
    queryFn: async () => {
      if (!customerId) throw new Error(t("phase8.owners.errors.noClinicSelected"));

      const { data, error } = await supabase
        .from("owners")
        .select(`
          *,
          owner_customer:owners_customers!inner(
            customer_id,
            deleted_at
          ),
          phones:owners_phones(*),
          emails:owners_emails(*),
          addresses:owners_addresses(*, city:cities(id, name, state_id, state:states(id, name, code)))
        `)
        .eq("id", id)
        .eq("owner_customer.customer_id", customerId)
        .is("owner_customer.deleted_at", null)
        .is("deleted_at", null)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { owner_customer: _ownerCustomer, ...rest } = data as any;

      return {
        ...rest,
      } as OwnerWithContacts;
    },
  });
};

// Criar owner
export const useCreateOwner = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");

  return useMutation({
    mutationFn: async (input: OwnerInput) => {
      const [{ data: authData }, customerId] = await Promise.all([
        supabase.auth.getUser(),
        getCurrentClinicCustomerId(),
      ]);

      let createdOwnerId: string | null = null;
      try {
        // 2. Criar owner
        const { data: owner, error: ownerError } = await supabase
          .from("owners")
          .insert({
            name: input.name,
            gender_id: input.gender_id || null,
            document_type_id: input.document_type_id || null,
            document: input.document || null,
            birth_date: input.birth_date || null,
            profession: input.profession || null,
            marketing_consent: input.marketing_consent,
            lead_source_id: input.lead_source_id || null,
            created_by: authData.user?.id ?? null,
          })
          .select()
          .single();

        if (ownerError) throw ownerError;
        createdOwnerId = owner.id;

        // 3. Vincular owner ao customer
        const { error: linkError } = await supabase
          .from("owners_customers")
          .insert({
            owner_id: owner.id,
            customer_id: customerId,
          });

        if (linkError) throw linkError;

        await Promise.all([
          input.phones.length > 0
            ? supabase.from("owners_phones").insert(
                input.phones.map((phone) => ({
                  owner_id: owner.id,
                  phone_number: phone.phone_number,
                  is_whatsapp: phone.is_whatsapp,
                  is_primary: phone.is_primary,
                })),
              ).then(({ error }) => {
                if (error) throw error;
              })
            : Promise.resolve(),
          input.emails.length > 0
            ? supabase.from("owners_emails").insert(
                input.emails.map((email) => ({
                  owner_id: owner.id,
                  email: email.email,
                  is_primary: email.is_primary,
                })),
              ).then(({ error }) => {
                if (error) throw error;
              })
            : Promise.resolve(),
          input.addresses && input.addresses.length > 0
            ? supabase.from("owners_addresses").insert(
                input.addresses.map((address) => ({
                  owner_id: owner.id,
                  zip_code: address.zip_code || null,
                  street: address.street || null,
                  number: address.number || null,
                  complement: address.complement || null,
                  neighborhood: address.neighborhood || null,
                  city_id: address.city_id || null,
                  is_primary: address.is_primary,
                })),
              ).then(({ error }) => {
                if (error) throw error;
              })
            : Promise.resolve(),
        ]);

        return owner;
      } catch (err) {
        // Best-effort cleanup to avoid leaving a partially-created owner if a later step fails.
        if (createdOwnerId) {
          await rollbackOwnerOrQueue(createdOwnerId, { maxAttempts: 3 });
        }
        throw err;
      }
    },
    onSuccess: () => {
      invalidatePatientsCache(queryClient);
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.owners.createErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// Atualizar owner
export const useUpdateOwner = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: OwnerInput }) => {
      const customerId = await getCurrentClinicCustomerId();

      // 1. Atualizar dados do owner
      const { error: ownerError } = await supabase
        .from("owners")
        .update({
          name: input.name,
          gender_id: input.gender_id || null,
          document_type_id: input.document_type_id || null,
          document: input.document || null,
          birth_date: input.birth_date || null,
          profession: input.profession || null,
          marketing_consent: input.marketing_consent,
          lead_source_id: input.lead_source_id || null,
        })
        .eq("id", id);

      if (ownerError) throw ownerError;

      // 2. owner<->customer link is managed elsewhere (no notes field)

      await Promise.all([
        supabase.from("owners_phones").delete().eq("owner_id", id),
        supabase.from("owners_emails").delete().eq("owner_id", id),
        supabase.from("owners_addresses").delete().eq("owner_id", id),
      ]);

      await Promise.all([
        input.phones.length > 0
          ? supabase.from("owners_phones").insert(
              input.phones.map((phone) => ({
                owner_id: id,
                phone_number: phone.phone_number,
                is_whatsapp: phone.is_whatsapp,
                is_primary: phone.is_primary,
              })),
            ).then(({ error }) => {
              if (error) throw error;
            })
          : Promise.resolve(),
        input.emails.length > 0
          ? supabase.from("owners_emails").insert(
              input.emails.map((email) => ({
                owner_id: id,
                email: email.email,
                is_primary: email.is_primary,
              })),
            ).then(({ error }) => {
              if (error) throw error;
            })
          : Promise.resolve(),
        input.addresses && input.addresses.length > 0
          ? supabase.from("owners_addresses").insert(
              input.addresses.map((address) => ({
                owner_id: id,
                zip_code: address.zip_code || null,
                street: address.street || null,
                number: address.number || null,
                complement: address.complement || null,
                neighborhood: address.neighborhood || null,
                city_id: address.city_id || null,
                is_primary: address.is_primary,
              })),
            ).then(({ error }) => {
              if (error) throw error;
            })
          : Promise.resolve(),
      ]);

      return id;
    },
    onSuccess: (id) => {
      invalidatePatientsCache(queryClient);
      queryClient.invalidateQueries({ queryKey: ["owner", id] });
      toast({
        title: t("phase8.owners.updatedTitle"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.owners.updateErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// Deletar owner (soft delete)
export const useDeleteOwner = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");

  return useMutation({
    mutationFn: async (id: string) => {
      // SECURITY DEFINER RPC: validates clinic access + patients:delete permission and cascades via deleted_at
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).rpc("soft_delete_owner", {
        p_owner_id: id,
      });

      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      invalidatePatientsCache(queryClient);
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["appointment"] });
      queryClient.invalidateQueries({ queryKey: ["grooming-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["grooming-session"] });
      queryClient.invalidateQueries({ queryKey: ["package-purchases"] });
      toast({
        title: t("phase8.owners.deletedTitle"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.owners.deleteErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
